import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getStatusColor } from '@t-one-internal/design-system';

interface ReviewProgressRow {
  flowStatus: string;
  employeeCount: number;
}

interface ReviewProgressChartProps {
  data: Array<Record<string, unknown>>;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; order: number }> = {
  Completed: { label: 'Completed', color: getStatusColor('completed'), order: 0 },
  'In Progress': { label: 'In Progress', color: getStatusColor('in_progress'), order: 1 },
  Assigned: { label: 'Not Started', color: getStatusColor('pending'), order: 2 },
};

const DEFAULT_STATUS = { label: 'Other', color: '#9CA3AF', order: 3 };

const toReviewProgressRow = (raw: Record<string, unknown>): ReviewProgressRow => ({
  flowStatus: (raw.flowStatus as string) ?? 'Unknown',
  employeeCount: Number(raw.employeeCount ?? 0),
});

export const ReviewProgressChart: React.FC<ReviewProgressChartProps> = ({ data }) => {
  const { chartData, total } = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { chartData: [], total: 0 };
    }

    const rows = data.map(toReviewProgressRow);
    const total = rows.reduce((sum, r) => sum + r.employeeCount, 0);

    const sorted = [...rows].sort((a, b) => {
      const oa = STATUS_CONFIG[a.flowStatus]?.order ?? DEFAULT_STATUS.order;
      const ob = STATUS_CONFIG[b.flowStatus]?.order ?? DEFAULT_STATUS.order;
      return oa - ob;
    });

    const chartData = sorted.map((row) => {
      const config = STATUS_CONFIG[row.flowStatus] ?? DEFAULT_STATUS;
      return {
        name: config.label,
        value: row.employeeCount,
        color: config.color,
        pct: total > 0 ? Math.round((row.employeeCount / total) * 100) : 0,
      };
    });

    return { chartData, total };
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>
        No review progress data available
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '32px', height: '100%', padding: '8px 0' }}>
      {/* Donut chart — scales with the container height */}
      <div style={{ aspectRatio: '1', height: '100%', maxHeight: '280px', flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="88%"
              strokeWidth={0}
            >
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '20px', minWidth: 0 }}>
        {chartData.map((entry) => (
          <div key={entry.name} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: entry.color,
                flexShrink: 0,
                marginTop: '6px',
              }}
            />
            <div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#111827', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                {entry.pct}%
              </div>
              <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '2px' }}>
                {entry.name} ({entry.value})
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
